import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Lock, LogIn, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { establishEmailLinkSession, cleanEmailLinkUrl } from '@/lib/authEmailLink';
import { supabase } from '@/lib/authBootstrap';
import { finishPasswordSetup } from '@/lib/passwordSetup';

export default function ResetPasswordPage() {
  const [, setLocation] = useLocation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const { toast } = useToast();

  const [sessionReady, setSessionReady] = useState(false);
  const link = useRef(window.location.href);
  useEffect(() => {
    let cancelled = false;
    establishEmailLinkSession(link.current, true)
      .then(({ intent }) => {
        if (intent === 'invite') throw new Error('Please use the invitation password setup page.');
        if (!cancelled) setSessionReady(true);
      })
      .catch((error) => {
        if (!cancelled) setError(error instanceof Error ? error.message : 'Please request a new reset link.');
      })
      .finally(() => { if (!cancelled) cleanEmailLinkUrl(); });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionReady) return;
    setIsLoading(true);
    setError('');

    // Validate passwords
    if (!newPassword.trim()) {
      setError('Please enter a new password');
      setIsLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      if (!passwordSaved) {
        const { error: updateError } = await supabase.auth.updateUser({
          password: newPassword, data: { requirePasswordChange: false }
        });
        if (updateError) throw new Error(updateError.message);
        setPasswordSaved(true);
      }
      const destination = await finishPasswordSetup();
      console.log('[RESET_PASSWORD] Password updated successfully');
      setSuccess(true);

      // Get user email before signing out
      let userEmail = '';
      try {
        const { data: { user } } = await supabase.auth.getUser();
        userEmail = user?.email || '';
      } catch (getUserError) {
        console.warn('[RESET_PASSWORD] Could not get user email, but continuing', getUserError);
      }

      // Sign out to force login with new credentials (helps password managers)
      try {
        await supabase.auth.signOut();
        console.log('[RESET_PASSWORD] Successfully signed out user');
      } catch (signOutError) {
        console.warn('[RESET_PASSWORD] Sign out failed, but continuing', signOutError);
      }

      toast({
        title: "Password reset successful",
        description: destination === '/auth/mobile-ready' ? 'Open the ReActor mobile app and sign in with your new password.' : 'Please sign in with your email and new password.',
      });

      // Force full page reload to login (ensures auth state is fully cleared)
      const loginUrl = destination === '/auth/mobile-ready' ? destination : userEmail ? `/login?email=${encodeURIComponent(userEmail)}` : '/login';
      setTimeout(() => {
        window.location.href = loginUrl;
      }, 2000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password';
      console.error('[RESET_PASSWORD] Unexpected error:', errorMessage);
      setError(errorMessage);
      toast({
        title: "Unexpected error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2">
              <LogIn className="h-8 w-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">ReActor</h1>
            </div>
          </div>
          <CardTitle className="text-2xl text-center">Set new password</CardTitle>
          <CardDescription className="text-center">
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate data-form-type="other">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                placeholder="Enter your new password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setPasswordSaved(false); }}
                disabled={isLoading || !sessionReady}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                placeholder="Confirm your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading || !sessionReady}
                required
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="bg-green-50 border-green-200">
                <AlertDescription className="text-green-800">
                  ✅ Password reset successful! Redirecting to login page...
                </AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={!sessionReady || isLoading || success || !newPassword.trim() || !confirmPassword.trim()}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating password...
                </>
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  Update password
                </>
              )}
            </Button>

            <div className="text-center">
              <Button
                variant="link"
                className="text-sm text-blue-600 hover:text-blue-500"
                type="button"
                onClick={async () => { await supabase.auth.signOut(); setLocation('/login'); }}
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back to login
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
